import * as _ from "lodash-es";
import { useContext, useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import IVendorModel from "../../../../models/IVendorModel";
import SortOrderEnum from "../../../../models/SortOrderEnum";
import UIService from "../../../../services/UIService";
import OwButton from "../../../shared/OwButton/OwButton";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import "./CatalogItemFilter.scss";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import OwHierarchicalCategorySelector from "@components/shared/OwHierarchicalCategorySelector/OwHierarchicalCategorySelector";
import ICategory from "@models/ICategory";
import IGetCategoriesDTO from "@models/DTO/IGetCategoriesDTO";
import IOnGetCategoriesDTO from "@models/DTO/IOnGetCategoriesDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import { CatalogContext, ICatalogFilterState } from "@contexts/CatalogContext";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    searchFn: (queryString?: string, reset?: boolean, offset?: number, categoryGuid?: string) => void;
    sortCol: string;
    sortDir: SortOrderEnum;
    limit: number;
    offset: number;
    emitOnLoad: boolean;
    catalogGuid: string;
};

export const CatalogItemFilter = (props: Props) => {
    const { supplierModel, userModel, vendorModel, socket, uiContext } = useAppContextNonNull();

    const catalogContext = useContext(CatalogContext);

    const [rootCategories, setRootCategories] = useState<ICategory[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<ICategory | undefined>(undefined);

    const [state, setState] = useState({
        foundVendors: [] as IVendorModel[],
        clearVendorTypeahead: false,
        selectedVendor: null as IVendorModel | null,
        connectedVendors: [] as IVendorModel[],
        dateSelectedValue: null,
        order_state_id: "",
        category_id: "",
        invoice_no: "",
        load_no: "",
        sample_no: "",
        item_name: catalogContext.filterState?.item_name || "",
        sku: catalogContext.filterState?.sku || "",
        description: catalogContext.filterState?.description || "",
        category: catalogContext.filterState?.category || "",
        isConnected: false,
        qbo_connected: catalogContext.filterState?.qbo_connected || (undefined as boolean | undefined),
        qbd_connected: catalogContext.filterState?.qbd_connected || (undefined as boolean | undefined),
        disableSearch: false,
        activeSearch: false,
        hasCommonItems: false,
        is_common_item: catalogContext.filterState?.is_common_item || false,
        type: catalogContext.filterState?.type || "",
        sortCol: props.sortCol || "name",
        sortDir: props.sortDir || SortOrderEnum.ASC,
        isVisible: true,
        active: catalogContext.filterState?.active !== undefined ? catalogContext.filterState.active : true,
        is_new_release: catalogContext.filterState?.is_new_release || false,
        is_ready_to_ship: catalogContext.filterState?.is_ready_to_ship || false,
        qbo_item_id: catalogContext.filterState?.qbo_item_id || "",
        limit: props.limit || 10,
        offset: props.offset || 0,
    });

    const getQueryString = (stateDetail: any) => {
        const query: string[] = [];
        let _queryString = "";

        if (!_.isNil(stateDetail.item_name) && stateDetail.item_name.length) {
            query.push(`item_name=${stateDetail.item_name}`);
        }

        if (!_.isNil(stateDetail.sku) && stateDetail.sku.length) {
            query.push(`sku=${stateDetail.sku}`);
        }

        if (!_.isNil(stateDetail.description) && stateDetail.description.length) {
            query.push(`description=${stateDetail.description}`);
        }

        if (!_.isNil(stateDetail.category) && stateDetail.category.length) {
            query.push(`category=${stateDetail.category}`);
        }

        if (!_.isNil(stateDetail.qbo_item_id) && stateDetail.qbo_item_id.length) {
            query.push(`qbo_item_id=${stateDetail.qbo_item_id}`);
        }

        if (!_.isNil(state.qbo_connected)) {
            query.push(`qbo_connected=${stateDetail.qbo_connected}`);
        }

        if (!_.isNil(stateDetail.qbd_connected)) {
            query.push(`qbd_connected=${stateDetail.qbd_connected}`);
        }

        if (!_.isNil(stateDetail.hasCommonItems)) {
            query.push(`is_common_item=${stateDetail.is_common_item}`);
        }

        if (!_.isNil(stateDetail.sortCol)) {
            query.push(`order_by=${stateDetail.sortCol}`);
        }

        if (!_.isNil(stateDetail.sortDir)) {
            query.push(`order_dir=${stateDetail.sortDir}`);
        }

        if (!_.isNil(stateDetail.active)) {
            query.push(`active=${stateDetail.active}`);
        }

        if (!_.isNil(stateDetail.type) && stateDetail.type.length) {
            query.push(`type=${stateDetail.type}`);
        }

        if (!_.isNil(stateDetail.is_new_release) && stateDetail.is_new_release) {
            query.push(`is_new_release=${stateDetail.is_new_release}`);
        }

        if (!_.isNil(stateDetail.is_ready_to_ship) && stateDetail.is_ready_to_ship) {
            query.push(`is_ready_to_ship=${stateDetail.is_ready_to_ship}`);
        }

        if (query.length) {
            _queryString = "?" + query.join("&");
        }

        return _queryString;
    };

    useEffect(() => {
        if (!_.isNil(socket) && supplierModel) {
            let hasCommonItems = false;

            if (supplierModel.show_common_items_modal) {
                hasCommonItems = true;
            }

            setState((prev) => ({
                ...prev,
                isConnected: true,
                hasCommonItems,
            }));

            if (props.emitOnLoad) {
                performSearch();
            }
        }
    }, []);

    useEffect(() => {
        if (props.sortDir !== state.sortDir || props.sortCol !== state.sortCol) {
            setState((prev) => ({
                ...prev,
                sortCol: props.sortCol,
                sortDir: props.sortDir,
            }));
            performSearch();
        }
    }, [props.sortDir, props.sortCol]);

    useEffect(() => {
        if (props.limit !== state.limit || props.offset !== state.offset) {
            setState((prev) => ({
                ...prev,
                limit: props.limit,
                offset: props.offset,
            }));
            performSearch(undefined, undefined, props.offset);
        }
    }, [props.limit, props.offset]);

    useEffect(() => {
        const onGetCategories = (data: IOnGetCategoriesDTO) => {
            setRootCategories(data.categories.filter((c) => !c.hide_category));
        };

        socket.on(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);

        const payload: IGetCategoriesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            catalogGuid: props.catalogGuid,
            onlyRootCategories: true,
        };

        socket.emit(EndpointsEnum.CategoriesGetCategories, payload);

        return () => {
            socket.removeListener(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);
        };
    }, [props.catalogGuid]);

    useEffect(() => {
        // Sync filter state to context
        if (catalogContext.setFilterState) {
            const filterState: ICatalogFilterState = {
                item_name: state.item_name,
                sku: state.sku,
                description: state.description,
                category: state.category,
                categoryGuid: selectedCategory?.guid,
                type: state.type,
                active: state.active,
                is_new_release: state.is_new_release,
                is_ready_to_ship: state.is_ready_to_ship,
                is_common_item: state.is_common_item,
                qbo_connected: state.qbo_connected,
                qbd_connected: state.qbd_connected,
                qbo_item_id: state.qbo_item_id,
            };
            catalogContext.setFilterState(filterState);
        }
    }, [
        catalogContext.setFilterState,
        state.item_name,
        state.sku,
        state.description,
        state.category,
        selectedCategory,
        state.type,
        state.active,
        state.is_new_release,
        state.is_ready_to_ship,
        state.is_common_item,
    ]);

    const handleChange = (e: any) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        setState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const performSearch = (_queryString?: string, reset?: boolean, offset?: number) => {
        // Always use the most up-to-date queryString built from current state
        const currentQueryString = _queryString || getQueryString(state);
        props.searchFn(currentQueryString, reset, offset, selectedCategory?.guid);
    };

    const resetSearch = () => {
        const updatedState = {
            ...state,
            item_name: "",
            sku: "",
            description: "",
            category: "",
            qbo_connected: undefined,
            qbd_connected: undefined,
            is_common_item: false,
            type: "",
            sortCol: "name",
            sortDir: SortOrderEnum.ASC,
            active: true,
            is_new_release: false,
            is_ready_to_ship: false,
            qbo_item_id: "",
        };

        setState(updatedState);
        setSelectedCategory(undefined);

        const updateQueryString = getQueryString(updatedState);

        performSearch(updateQueryString, true);
    };

    const toggleVisible = () => {
        setState((prev) => ({
            ...prev,
            isVisible: !prev.isVisible,
        }));
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter") {
            performSearch(undefined, undefined, 0);
        }
    };

    let cardClass = "dark-card text-white bg-secondary";
    if (UIService.isMainViewLight()) {
        cardClass = "";
    }

    const typeOptions = [
        { title: "kit", value: "kit" },
        { title: "inventory", value: "inventory" },
        { title: "non_inventory", value: "non_inventory" },
        { title: "product_group", value: "product_group" },
        { title: "product_group_item", value: "product_group_item" },
    ];

    return (
        <div className="CatalogItemFilter row">
            <div className="row mb-2 d-flex align-items-end">
                <div className="col-12 col-md-2">
                    <OwLabel className={"mb-0 mt-2"} title={"item_name"} description={"Item Name Title"} />
                    <OwInputWrapper
                        className="form-control form-control-sm"
                        name="item_name"
                        value={state.item_name}
                        fn={handleChange}
                        onKeyDownFn={enterSearch}
                    />
                </div>
                <div className="col-12 col-md-2">
                    <OwLabel className={"mb-0 mt-2"} title={"sku"} description={"SKU Title"} />
                    <OwInputWrapper
                        className="form-control form-control-sm"
                        name="sku"
                        value={state.sku}
                        fn={handleChange}
                        onKeyDownFn={enterSearch}
                    />
                </div>
                <div className="col-12 col-md-2">
                    <OwLabel className={"mb-0 mt-2"} title={"description"} description={"Description Title"} />
                    <OwInputWrapper
                        className="form-control form-control-sm"
                        name="description"
                        value={state.description}
                        fn={handleChange}
                        onKeyDownFn={enterSearch}
                    />
                </div>
                <div className="col-12 col-md-2">
                    <OwLabel className={"mb-0 mt-2"} title={"select_category"} description={"Category Title"} />
                    <OwHierarchicalCategorySelector
                        categories={rootCategories}
                        value={selectedCategory}
                        onChange={(category) => setSelectedCategory(category)}
                        placeholder="select_category"
                        userModel={userModel}
                        supplierModel={supplierModel}
                        disabled={state.category.length > 0}
                    />
                </div>
                <div className="col-12 col-md-2">
                    <OwLabel className={"mb-0 mt-2"} title={"category"} description={"Category Title"} />
                    <OwInputWrapper
                        className="form-control form-control-sm"
                        name="category"
                        value={state.category}
                        fn={handleChange}
                        onKeyDownFn={enterSearch}
                        disabled={!!selectedCategory}
                    />
                </div>
                <div className={"col-12 col-md-2 "}>
                    <OwLabel className={"mb-0 mt-2"} title={"type"} description={"Type Title"} />
                    <OwSelectOption
                        fn={handleChange}
                        title={"type"}
                        name={"type"}
                        options={typeOptions}
                        valueProp={"value"}
                        titleProp={"title"}
                        showBlankFirst={true}
                        translateTitle={true}
                        selectedOption={state}
                        selectedOptionProp={"type"}
                    />
                </div>
                <div className={"col-12 col-md-2 "}>
                    <OwToggle active={state.active} fn={handleChange} name={"active"} label={"only_active"} labelOnRight={true} />
                </div>
                {supplierModel.has_new_releases_category ? (
                    <div className={"col-12 col-md-2 "}>
                        <OwCheckbox
                            className={"mb-3"}
                            falsyVal={false}
                            truthyVal={true}
                            fn={handleChange}
                            label={"is_new_release"}
                            name={"is_new_release"}
                            checked={state.is_new_release}
                        />
                    </div>
                ) : null}
                {supplierModel.has_ready_to_ship_category ? (
                    <div className={"col-12 col-md-2 "}>
                        <OwCheckbox
                            className={"mb-3"}
                            falsyVal={false}
                            truthyVal={true}
                            fn={handleChange}
                            label={"is_ready_to_ship"}
                            name={"is_ready_to_ship"}
                            checked={state.is_ready_to_ship}
                        />
                    </div>
                ) : null}
                {state.hasCommonItems ? (
                    <div className={"col-12 col-md-2 "}>
                        <OwCheckbox
                            className={"mb-3"}
                            falsyVal={false}
                            truthyVal={true}
                            fn={handleChange}
                            label={"only_common_items"}
                            name={"is_common_item"}
                            checked={state.is_common_item}
                        />
                    </div>
                ) : null}
                {supplierModel.Supplier_Integration.has_qbo ? (
                    <div className={"col-12 col-md-2"}>
                        <fieldset className={"d-inline-block border p-2 me-1"}>
                            <legend className={"mb-0 w-auto"}>
                                <OwLabel className={"ps-1 pe-1 text-small"} title={"quickbooks_online"} noBottomMargin={true} />
                            </legend>
                            <div className={"d-flex align-items-center"}>
                                <div>
                                    <OwLabel title={"connected"} />
                                    <OwSelectOption
                                        fn={handleChange}
                                        name={"qbo_connected"}
                                        options={[
                                            { value: true, title: "Y" },
                                            { value: false, title: "N" },
                                        ]}
                                        titleProp={"title"}
                                        valueProp={"value"}
                                    />
                                </div>
                                {uiContext.showHiddenFeatures ? (
                                    <div className={"ms-2"}>
                                        <label>Item ID</label>
                                        <OwInputWrapper
                                            name={"qbo_item_id"}
                                            value={state.qbo_item_id}
                                            fn={handleChange}
                                            onKeyDownFn={enterSearch}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </fieldset>
                    </div>
                ) : null}

                {supplierModel.Supplier_Integration.has_qbd ? (
                    <div className={"col-12 col-md-2"}>
                        <fieldset className={"d-inline-block border p-2 me-1"}>
                            <legend className={"mb-0 w-auto"}>
                                <OwLabel className={"ps-1 pe-1 text-small"} title={"quickbooks_desktop"} noBottomMargin={true} />
                            </legend>
                            <OwLabel title={"connected"} />
                            <OwSelectOption
                                fn={handleChange}
                                name={"qbd_connected"}
                                options={[
                                    { value: true, title: "Y" },
                                    { value: false, title: "N" },
                                ]}
                                titleProp={"title"}
                                valueProp={"value"}
                            />
                        </fieldset>
                    </div>
                ) : null}

                <div className="col-12 col-md-2">
                    <label>&nbsp;</label>

                    <div>
                        <OwButton
                            disabled={state.disableSearch}
                            disableOnActive={true}
                            active={catalogContext.activeProcess}
                            title="search"
                            fn={(e: any) => {
                                performSearch(undefined, undefined, 0);
                            }}
                        />

                        <OwButton className={"btn-danger ms-2"} title={"reset"} fn={resetSearch} disabled={catalogContext.activeProcess} />
                    </div>
                </div>
            </div>
        </div>
    );
};
